import { z } from "zod";

export const OeePeriodSchema = z.enum(["8h", "24h", "7d", "30d"]);
export type OeePeriod = z.infer<typeof OeePeriodSchema>;

const BoolStringSchema = z
  .enum(["true", "false"])
  .optional()
  .default("false")
  .transform((v) => v === "true");

export const OeeQuerySchema = z.object({
  period: OeePeriodSchema.optional().default("24h"),
  compareWithPrevious: BoolStringSchema,
  breakdown: BoolStringSchema,
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});

export const OeeMetricBreakdownSchema = z.object({
  formula: z.string(),
  inputs: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
  computed: z.number().nullable(),
});
export type OeeMetricBreakdown = z.infer<typeof OeeMetricBreakdownSchema>;

export const OeeConfidenceBreakdownSchema = z.object({
  formula: z.string(),
  inputs: z.record(z.string(), z.union([z.number(), z.string(), z.boolean(), z.null()])),
  computedLevel: z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]),
});
export type OeeConfidenceBreakdown = z.infer<typeof OeeConfidenceBreakdownSchema>;

export const OeeBreakdownDetailSchema = z.object({
  availability: OeeMetricBreakdownSchema,
  performance: OeeMetricBreakdownSchema,
  quality: OeeMetricBreakdownSchema,
  confidence: OeeConfidenceBreakdownSchema,
  oee: OeeMetricBreakdownSchema,
});
export type OeeBreakdownDetail = z.infer<typeof OeeBreakdownDetailSchema>;

const NullableFinite = z.number().finite().nullable();

export const PlanAdherenceSchema = z
  .object({
    value: NullableFinite,
    basis: z.enum(["in-progress", "completed"]).nullable(),
    overrunPct: NullableFinite,
  })
  .nullable();
export type PlanAdherence = z.infer<typeof PlanAdherenceSchema>;

export const PlanAdherenceBreakdownSchema = z
  .object({
    formula: z.string(),
    plannedQty: NullableFinite,
    producedQty: NullableFinite,
    fractionElapsed: NullableFinite,
    plannedStartAt: z.string().datetime({ offset: true }).nullable(),
    expectedEnd: z.string().datetime({ offset: true }).nullable(),
    plannedStartEstimated: z.boolean(),
    expectedEndEstimated: z.boolean(),
    computed: NullableFinite,
  })
  .nullable();
export type PlanAdherenceBreakdown = z.infer<typeof PlanAdherenceBreakdownSchema>;

export const OeeRawSchema = z.object({
  plannedMinutes: z.number().finite(),
  operatingMinutes: z.number().finite(),
  downtimeMinutes: z.number().finite(),
  openStops: z.number().finite(),
  plannedQty: z.number().finite(),
  producedQty: z.number().finite(),
  runCount: z.number().finite(),
  totalLoss: z.number().finite(),
  hasManualEntries: z.boolean(),
  nodataMachineRatio: z.number().finite(),
  nodataMinutes: z.number().finite(),
});

export const OeePreviousSchema = z
  .object({
    oee: NullableFinite,
    availability: NullableFinite,
    performance: NullableFinite,
    quality: NullableFinite,
  })
  .nullable();

// ── A BASE do tempo planejado ───────────────────────────────────────────────────────────
// Dado DERIVADO (não persistido) que responde "de onde saiu o denominador da Disponibilidade?".
// Existe porque a mesma leitura passou a ter três origens possíveis, e um número sem origem
// declarada é o que o Princípio IV proíbe: degradação silenciosa. Sem ele, uma linha sem expediente
// cadastrado exibiria um indicador plausível sem dizer que está usando a janela da run como plano.
//  calendar — o expediente declarado da linha
//  run — a janela planejada da run, porque não há calendário ou porque o calendário
//  declara a janela fechada e existe run planejada
//  none — não há expediente nem run ⇒ tempo planejado 0 ⇒ A e P indisponíveis
export const PlannedBasisSchema = z.enum(["calendar", "run", "none"]);
export type PlannedBasis = z.infer<typeof PlannedBasisSchema>;

// DE QUEM é o expediente. Campo IRMÃO de `plannedBasis`, não um valor novo dentro dele.
// `plannedBasis` responde "isto é calendarizado?" e não muda de forma — acrescentar `"organization"`
// ao enum quebraria todo consumidor que hoje trata `"calendar"` como o caso normal, e o badge
// perderia os três casos estabelecidos. A pergunta nova é outra: com o expediente podendo
// vir da fábrica, "por que esse número mudou?" só tem resposta se a origem estiver declarada.
// Correspondência garantida (teste em oee.service.test.ts):
//   plannedBasis = "calendar"        ⇒ calendarSource ∈ {"line", "organization"}
//   plannedBasis ∈ {"run", "none"}   ⇒ calendarSource = null
export const CalendarSourceSchema = z.enum(["line", "organization"]).nullable();
export type CalendarSource = z.infer<typeof CalendarSourceSchema>;

export const OeeResponseSchema = z.object({
  period: OeePeriodSchema,
  oee: NullableFinite,
  availability: NullableFinite,
  performance: NullableFinite,
  quality: NullableFinite,
  confidenceLevel: z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]),
  reasons: z.array(z.string()),
  diagnostic: z.string(),
  plannedBasis: PlannedBasisSchema,
  // Aditivo e nullable ⇒ resposta em cache de antes do deploy segue válida (o campo chega
  // `undefined`, e quem lê trata a ausência como "origem desconhecida", não como erro).
  calendarSource: CalendarSourceSchema.default(null),
  raw: OeeRawSchema,
  previous: OeePreviousSchema.optional(),
  deltaPp: NullableFinite.optional(),
  breakdown: OeeBreakdownDetailSchema.optional(),
  planAdherence: PlanAdherenceSchema.optional(),
  planAdherenceBreakdown: PlanAdherenceBreakdownSchema.optional(),
});
export type OeeResponse = z.infer<typeof OeeResponseSchema>;

// Decomposição do OEE por máquina (GET /lines/:lineId/oee/by-machine).
// Disponibilidade + Qualidade + refugo por máquina. SEM número de OEE composto por máquina
// (Desempenho fora de escopo); o FE exibe Desempenho como "—".
export const MachineOeeQuerySchema = z.object({
  period: OeePeriodSchema.optional().default("24h"),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type MachineOeeQuery = z.infer<typeof MachineOeeQuerySchema>;

const NonNegInt = z.number().int().nonnegative();
// Spec 081 (FR-027) — as QUANTIDADES da decomposição por máquina viraram NUMERIC(14,3) em
// `calculate_machine_oee`. `int()` aqui é schema de RESPOSTA: a primeira máquina que reportar
// fração faz o serializer recusar o payload inteiro e o endpoint devolver 500 — exatamente nas
// linhas para as quais a feature foi escrita. Minutos continuam inteiros (são arredondados no SQL).
const NonNegQty = z.number().nonnegative().finite();

export const MachineOeeBreakdownItemSchema = z.object({
  machineId: z.string().uuid(),
  machineName: z.string(),
  isProductionCounter: z.boolean(),
  availability: NullableFinite, // NULL ⇒ sem janela planejada
  quality: NullableFinite, // NULL ⇒ produzido=0 ("insuficiente")
  producedQty: NonNegQty,
  scrapQty: NonNegQty,
  scrapReject: NonNegQty,
  scrapWaste: NonNegQty,
  scrapRework: NonNegQty,
  goodQty: NonNegQty,
  plannedMinutes: NonNegInt,
  operatingMinutes: NonNegInt,
});
export type MachineOeeBreakdownItem = z.infer<typeof MachineOeeBreakdownItemSchema>;

export const MachineOeeBreakdownResponseSchema = z.object({
  period: OeePeriodSchema,
  items: z.array(MachineOeeBreakdownItemSchema),
  // A base do tempo planejado é propriedade da LINHA (é o calendário dela que resolve), então vive
  // no envelope e não no item — repeti-la por máquina sugeriria que máquinas da mesma linha podem
  // ter bases diferentes, que é justamente a incoerência que a decomposição existe para evitar.
  plannedBasis: PlannedBasisSchema,
  // Mesma razão para a origem: quem resolve as camadas é a linha, não a máquina.
  calendarSource: CalendarSourceSchema.default(null),
});
export type MachineOeeBreakdownResponse = z.infer<typeof MachineOeeBreakdownResponseSchema>;

export const MachineOeeBreakdownEnvelopeSchema = z.object({
  data: MachineOeeBreakdownResponseSchema,
  error: z.null(),
});
